import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useLocation, useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { IoArrowBack, IoChatbubble, IoLogoApple, IoCheckmarkCircle, IoDocumentTextOutline, IoCloseOutline } from 'react-icons/io5';
import { FcGoogle } from 'react-icons/fc';
import {
  FaTruck, FaBroom, FaWifi, FaPaintRoller, FaSnowflake, FaGlassWater, FaDesktop,
} from 'react-icons/fa6';
import { signOut } from 'firebase/auth';
import { doc, updateDoc } from 'firebase/firestore';
import { ref as storageRef, uploadBytes, getDownloadURL } from 'firebase/storage';
import { auth, db, storage } from '../../../firebase/config';
import { signupSocialUser } from '../../../services/authService';
import { setConsentPath } from '../../../services/consentService';
import { PARTNER_SIGNUP_CATEGORIES } from '../../../config/ieumConfig';
import AlertModal from '../../../components/common/AlertModal';

const ICON_MAP = { FaTruck, FaBroom, FaWifi, FaPaintRoller, FaSnowflake, FaGlassWater, FaDesktop };

const PROVIDER_META = {
  kakao: { label: '카카오', badgeIcon: IoChatbubble, badgeColor: '#000' },
  google: { label: '구글', badgeIcon: FcGoogle },
  apple: { label: '애플', badgeIcon: IoLogoApple, badgeColor: '#000' },
};

const PARTNER_TERMS = [
  { key: 'age_consent', label: '만 14세 이상이며 모두 동의합니다', required: true, noLink: true },
  { key: 'service', label: '서비스 이용약관', required: true },
  { key: 'privacy', label: '개인정보 처리방침', required: true },
  { key: 'partner_contract', label: '제휴업체 이용약관 등 계약 동의', required: true },
  { key: 'purchase_refund', label: '구매 및 환불 동의', required: true },
  { key: 'operation_policy', label: '운영정책', required: true },
  { key: 'marketing', label: '광고성 정보 수신 등 수신에 모두 동의합니다', required: false },
];

const REALTOR_TERMS = [
  { key: 'age_consent', label: '만 14세 이상이며 모두 동의합니다', required: true, noLink: true },
  { key: 'service', label: '서비스 이용약관', required: true },
  { key: 'privacy', label: '개인정보 처리방침', required: true },
  { key: 'realtor_consent', label: '공인중개사 동의서', required: true },
  { key: 'marketing', label: '광고성 정보 수신 등 수신에 모두 동의합니다', required: false },
];

const formatPhone = (v) => {
  const d = (v || '').replace(/\D/g, '').slice(0, 11);
  if (d.length < 4) return d;
  if (d.length < 8) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 7)}-${d.slice(7)}`;
};

const formatBizNo = (v) => {
  const d = (v || '').replace(/\D/g, '').slice(0, 10);
  if (d.length < 4) return d;
  if (d.length < 6) return `${d.slice(0, 3)}-${d.slice(3)}`;
  return `${d.slice(0, 3)}-${d.slice(3, 5)}-${d.slice(5)}`;
};

const SocialSignupPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { role: roleParam } = useParams();
  const state = location.state || {};
  const { authUid, email, displayName, provider, phone: statePhone, verifiedToken } = state;

  const role = roleParam === 'partner' ? 'partner' : 'realtor';
  const isPartner = role === 'partner';
  const termsConfig = isPartner ? PARTNER_TERMS : REALTOR_TERMS;
  const phoneLocked = !!verifiedToken;

  // 공통
  const [name, setName] = useState(displayName || '');
  const [phone, setPhone] = useState(statePhone ? formatPhone(statePhone) : '');

  // 제휴업체 전용
  const [businessName, setBusinessName] = useState('');
  const [contactName, setContactName] = useState('');
  const [contactPhone, setContactPhone] = useState('');
  const [bizNo, setBizNo] = useState('');
  const [bizPhone, setBizPhone] = useState('');
  const [address, setAddress] = useState('');
  const [services, setServices] = useState([]);
  const [partnerLicenseFile, setPartnerLicenseFile] = useState(null);
  const partnerLicenseInputRef = useRef(null);

  // 공인중개사 전용
  const [userType, setUserType] = useState('personal'); // personal | business
  const [officeName, setOfficeName] = useState('');
  const [licenseNo, setLicenseNo] = useState('');
  const [officeAddress, setOfficeAddress] = useState('');
  const [region, setRegion] = useState('');
  const [realtorBusinessNumber, setRealtorBusinessNumber] = useState('');
  const [realtorLicenseFile, setRealtorLicenseFile] = useState(null);
  const realtorLicenseInputRef = useRef(null);

  // 약관
  const [terms, setTerms] = useState(() =>
    termsConfig.reduce((acc, t) => ({ ...acc, [t.key]: false }), {})
  );

  const [submitting, setSubmitting] = useState(false);
  const [alert, setAlert] = useState(null);

  useEffect(() => {
    if (!authUid || !provider) {
      navigate('/login', { replace: true });
    }
  }, [authUid, provider, navigate]);

  const allAgreed = useMemo(
    () => termsConfig.every((t) => terms[t.key]),
    [terms, termsConfig]
  );
  const requiredAgreed = useMemo(
    () => termsConfig.filter((t) => t.required).every((t) => terms[t.key]),
    [terms, termsConfig]
  );

  const toggleAll = () => {
    const next = !allAgreed;
    setTerms(termsConfig.reduce((acc, t) => ({ ...acc, [t.key]: next }), {}));
  };

  const toggleOne = (key) => {
    setTerms((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const toggleService = (id) => {
    setServices((prev) => (prev.includes(id) ? prev.filter((s) => s !== id) : [...prev, id]));
  };

  const handleLicenseChange = (setter) => (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setAlert({ type: 'error', title: '파일 크기 초과', message: '10MB 이하 파일만 업로드 가능합니다.' });
      return;
    }
    setter(file);
  };

  const realtorBizNoDigits = realtorBusinessNumber.replace(/\D/g, '');
  const realtorBusinessValid = userType !== 'business' || (realtorBizNoDigits.length === 10 && !!realtorLicenseFile);

  const canSubmit = useMemo(() => {
    if (!name.trim()) return false;
    if (!phone.replace(/\D/g, '')) return false;
    if (!requiredAgreed) return false;
    if (isPartner) {
      if (!businessName.trim()) return false;
      if (services.length === 0) return false;
    } else {
      if (!officeName.trim()) return false;
      if (!realtorBusinessValid) return false;
    }
    return true;
  }, [name, phone, requiredAgreed, isPartner, businessName, services, officeName, realtorBusinessValid]);

  if (!authUid || !provider) return null;

  const providerMeta = PROVIDER_META[provider] || PROVIDER_META.google;

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setSubmitting(true);
    try {
      const profile = isPartner
        ? {
            contactName: contactName.trim(),
            contactPhone: contactPhone.replace(/\D/g, ''),
            bizNo: bizNo.replace(/\D/g, ''),
            business_number: bizNo.replace(/\D/g, ''),
            bizPhone: bizPhone.replace(/\D/g, ''),
            office_phone: bizPhone.replace(/\D/g, ''),
            address: address.trim(),
            business_address: address.trim(),
            services,
            service_categories: services,
            license_file_name: partnerLicenseFile?.name || '',
          }
        : {
            user_type: userType,
            officeName: officeName.trim(),
            business_number: userType === 'business' ? realtorBizNoDigits : '',
            businessNumber: userType === 'business' ? realtorBizNoDigits : '',
            licenseNo: licenseNo.replace(/\D/g, ''),
            officeAddress: officeAddress.trim(),
            address: officeAddress.trim(),
            region: region.trim(),
            license_file_name: realtorLicenseFile?.name || '',
          };

      await signupSocialUser({
        authUid,
        email: email || '',
        name: name.trim(),
        phone: phone.replace(/\D/g, ''),
        businessName: isPartner ? businessName.trim() : officeName.trim(),
        role,
        provider,
        verifiedToken,
        profile,
      });

      // 사업자등록증 업로드 (Storage + users 관련 role 문서에 URL 저장)
      try {
        const uploadFile = isPartner ? partnerLicenseFile : realtorLicenseFile;
        if (uploadFile && authUid) {
          const ext = uploadFile.name.split('.').pop();
          const folder = isPartner ? 'partners' : 'realtors';
          const fileRef = storageRef(storage, `${folder}/${authUid}/license.${ext}`);
          await uploadBytes(fileRef, uploadFile);
          const url = await getDownloadURL(fileRef);
          await updateDoc(doc(db, folder, authUid), { license_url: url });
        }
      } catch (uploadErr) {
        console.error('사업자등록증 업로드 실패:', uploadErr);
      }

      // 제휴업체: 승인 대기 → 완료 페이지
      // 공인중개사: 즉시 active → 역할별 홈
      setConsentPath('oauth-consent');
      navigate('/social/signup/complete', {
        state: {
          role,
          provider,
          name: name.trim(),
        },
        replace: true,
      });
    } catch (err) {
      setAlert({
        type: 'error',
        title: '가입에 실패했습니다',
        message: err?.message || '잠시 후 다시 시도해 주세요.',
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleBack = async () => {
    const ok = window.confirm('가입을 취소하고 로그인 화면으로 돌아갈까요?');
    if (!ok) return;
    try { await signOut(auth); } catch {}
    navigate('/login', { replace: true });
  };

  return (
    <Wrapper>
      <Card>
        <HeaderRow>
          <BackBtn onClick={() => navigate(-1)}>
            <IoArrowBack size={16} />
            <span>회원가입</span>
          </BackBtn>
        </HeaderRow>

        <ProviderChip>
          <providerMeta.badgeIcon size={16} color={providerMeta.badgeColor} />
          <span>{providerMeta.label} 소셜 회원가입</span>
        </ProviderChip>

        <Title>이음 파트너에 오신 것을 환영합니다</Title>
        <SubTitle>간편하게 가입하고 서비스를 시작해 보세요.</SubTitle>

        {/* 1. 기본 정보 */}
        <Section>
          <SectionTitle>
            <Num>1</Num> 기본 정보
          </SectionTitle>
          <FieldGrid>
            <Field $full>
              <Label>
                {isPartner ? '대표자명' : '이름'} <Req>*</Req>
              </Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="홍길동" />
            </Field>
            <Field $full>
              <Label>
                휴대폰 <Req>*</Req>
                {phoneLocked && <VerifiedBadge>✓ 인증완료</VerifiedBadge>}
              </Label>
              <Input
                value={phone}
                onChange={(e) => {
                  if (phoneLocked) return;
                  setPhone(formatPhone(e.target.value));
                }}
                placeholder="010-1234-5678"
                inputMode="numeric"
                readOnly={phoneLocked}
                $locked={phoneLocked}
              />
            </Field>
          </FieldGrid>
        </Section>

        {/* 2. 제휴업체 / 공인중개사 별 정보 */}
        {isPartner ? (
          <Section>
            <SectionTitle>
              <Num>2</Num> 사업자 정보
            </SectionTitle>
            <FieldGrid>
              <Field $full>
                <Label>업체명 <Req>*</Req></Label>
                <Input value={businessName} onChange={(e) => setBusinessName(e.target.value)} placeholder="정식 사업자명" />
              </Field>
              <Field>
                <Label>담당자명 <Hint>(선택)</Hint></Label>
                <Input value={contactName} onChange={(e) => setContactName(e.target.value)} placeholder="미입력 시 대표자명 사용" />
              </Field>
              <Field>
                <Label>담당자 휴대폰 <Hint>(선택)</Hint></Label>
                <Input value={contactPhone} onChange={(e) => setContactPhone(formatPhone(e.target.value))} placeholder="미입력 시 대표자 번호 사용" inputMode="numeric" />
              </Field>
              <Field>
                <Label>사업자등록번호 <Hint>(선택)</Hint></Label>
                <Input value={bizNo} onChange={(e) => setBizNo(formatBizNo(e.target.value))} placeholder="000-00-00000" inputMode="numeric" />
              </Field>
              <Field>
                <Label>대표 전화 <Hint>(선택)</Hint></Label>
                <Input value={bizPhone} onChange={(e) => setBizPhone(formatPhone(e.target.value))} placeholder="02-123-4567" inputMode="numeric" />
              </Field>
              <Field $full>
                <Label>사업장 주소 <Hint>(선택)</Hint></Label>
                <Input value={address} onChange={(e) => setAddress(e.target.value)} placeholder="주소" />
              </Field>
              <Field $full>
                <Label>사업자등록증 <Hint>(선택, PDF/JPG/PNG 10MB 이하)</Hint></Label>
                <FileRow>
                  <FilePreview $selected={!!partnerLicenseFile}>
                    {partnerLicenseFile ? (
                      <>
                        <IoDocumentTextOutline size={16} />
                        <span>{partnerLicenseFile.name}</span>
                        <FileClearBtn type="button" onClick={() => setPartnerLicenseFile(null)}>
                          <IoCloseOutline size={14} />
                        </FileClearBtn>
                      </>
                    ) : (
                      <span>파일을 선택해주세요</span>
                    )}
                  </FilePreview>
                  <FilePickBtn type="button" onClick={() => partnerLicenseInputRef.current?.click()}>
                    파일 선택
                  </FilePickBtn>
                  <input
                    ref={partnerLicenseInputRef}
                    type="file"
                    accept=".pdf,.jpg,.jpeg,.png"
                    onChange={handleLicenseChange(setPartnerLicenseFile)}
                    style={{ display: 'none' }}
                  />
                </FileRow>
              </Field>
            </FieldGrid>
          </Section>
        ) : (
          <>
            <Section>
              <SectionTitle>
                <Num>2</Num> 회원 유형
              </SectionTitle>
              <SectionSub>최초 가입 시에만 선택할 수 있으며, 이후 변경이 어렵습니다.</SectionSub>
              <TypeGrid>
                {[
                  { value: 'personal', label: '개인' },
                  { value: 'business', label: '사업자' },
                ].map((opt) => (
                  <TypeBtn
                    key={opt.value}
                    type="button"
                    $active={userType === opt.value}
                    onClick={() => setUserType(opt.value)}
                  >
                    {opt.label}
                  </TypeBtn>
                ))}
              </TypeGrid>
            </Section>

            <Section>
              <SectionTitle>
                <Num>3</Num> 중개사무소 정보
              </SectionTitle>
              <FieldGrid>
                <Field $full>
                  <Label>중개사무소명 <Req>*</Req></Label>
                  <Input value={officeName} onChange={(e) => setOfficeName(e.target.value)} placeholder="공인중개사사무소 이름" />
                </Field>
                {userType === 'business' && (
                  <>
                    <Field $full>
                      <Label>사업자 등록번호 <Req>*</Req></Label>
                      <Input
                        value={realtorBusinessNumber}
                        onChange={(e) => setRealtorBusinessNumber(formatBizNo(e.target.value))}
                        placeholder="000-00-00000"
                        inputMode="numeric"
                      />
                    </Field>
                    <Field $full>
                      <Label>사업자 등록증 <Req>*</Req> <Hint>(PDF/JPG/PNG 10MB 이하)</Hint></Label>
                      <FileRow>
                        <FilePreview $selected={!!realtorLicenseFile}>
                          {realtorLicenseFile ? (
                            <>
                              <IoDocumentTextOutline size={16} />
                              <span>{realtorLicenseFile.name}</span>
                              <FileClearBtn type="button" onClick={() => setRealtorLicenseFile(null)}>
                                <IoCloseOutline size={14} />
                              </FileClearBtn>
                            </>
                          ) : (
                            <span>파일을 선택해주세요</span>
                          )}
                        </FilePreview>
                        <FilePickBtn type="button" onClick={() => realtorLicenseInputRef.current?.click()}>
                          파일 선택
                        </FilePickBtn>
                        <input
                          ref={realtorLicenseInputRef}
                          type="file"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={handleLicenseChange(setRealtorLicenseFile)}
                          style={{ display: 'none' }}
                        />
                      </FileRow>
                    </Field>
                  </>
                )}
                <Field>
                  <Label>중개사 등록번호 <Hint>(선택)</Hint></Label>
                  <Input value={licenseNo} onChange={(e) => setLicenseNo(e.target.value.replace(/\D/g, '').slice(0, 15))} placeholder="등록번호" inputMode="numeric" />
                </Field>
                <Field>
                  <Label>사무소 주소 <Hint>(선택)</Hint></Label>
                  <Input value={officeAddress} onChange={(e) => setOfficeAddress(e.target.value)} placeholder="주소" />
                </Field>
                <Field $full>
                  <Label>활동 지역 <Hint>(선택)</Hint></Label>
                  <Input value={region} onChange={(e) => setRegion(e.target.value)} placeholder="예: 서울 강남구" />
                </Field>
              </FieldGrid>
            </Section>
          </>
        )}

        {/* 3. 제휴업체 — 제공 서비스 */}
        {isPartner && (
          <Section>
            <SectionTitle>
              <Num>3</Num> 제공 서비스 <Req>*</Req>
            </SectionTitle>
            <SectionSub>파트너님이 고객에게 제공 가능하신 모든 업종을 선택해 주세요.</SectionSub>
            <ServiceGrid>
              {PARTNER_SIGNUP_CATEGORIES.map((c) => {
                const Icon = ICON_MAP[c.icon];
                const active = services.includes(c.id);
                return (
                  <ServiceItem key={c.id} $active={active} onClick={() => toggleService(c.id)}>
                    {Icon && <Icon size={16} />}
                    <span>{c.label}</span>
                  </ServiceItem>
                );
              })}
            </ServiceGrid>
          </Section>
        )}

        {/* 약관 동의 */}
        <Section>
          <SectionTitle>
            <Num>4</Num> 약관 동의
          </SectionTitle>
          <TermsAll onClick={toggleAll}>
            <Check $checked={allAgreed}>
              {allAgreed && <IoCheckmarkCircle size={20} color="#4A6CF7" />}
            </Check>
            <span>전체 동의</span>
          </TermsAll>
          <TermsList>
            {termsConfig.map((t) => (
              <TermsRow key={t.key}>
                <Check
                  $checked={!!terms[t.key]}
                  onClick={() => toggleOne(t.key)}
                >
                  {terms[t.key] && <IoCheckmarkCircle size={18} color="#4A6CF7" />}
                </Check>
                <TermsLabel onClick={() => toggleOne(t.key)}>
                  [{t.required ? '필수' : '선택'}] {t.label}
                </TermsLabel>
                {!t.noLink && (
                  <TermsLink to={`/terms/${t.key}`} target="_blank">약관보기</TermsLink>
                )}
              </TermsRow>
            ))}
          </TermsList>
        </Section>

        <SubmitBtn disabled={!canSubmit || submitting} onClick={handleSubmit}>
          {submitting ? '가입 신청 중...' : '가입 완료'}
        </SubmitBtn>

        <FooterHint>
          이미 계정이 있으신가요?{' '}
          <FooterLink onClick={handleBack}>로그인하기</FooterLink>
        </FooterHint>
      </Card>

      {alert && (
        <AlertModal
          type={alert.type}
          title={alert.title}
          message={alert.message}
          onConfirm={() => setAlert(null)}
        />
      )}
    </Wrapper>
  );
};

export default SocialSignupPage;

const Wrapper = styled.div`
  min-height: 100vh;
  padding: ${({ theme }) => theme.spacing.lg};
  background: ${({ theme }) => theme.colors.bg};
  display: flex;
  justify-content: center;

  @media (min-width: 1024px) {
    background: #2f3542;
    padding: 40px 24px;
  }
`;

const Card = styled.div`
  width: 100%;
  max-width: 640px;
  background: #fff;
  border-radius: ${({ theme }) => theme.radius.lg};
  padding: ${({ theme }) => theme.spacing.xl};

  @media (min-width: 1024px) {
    padding: 48px;
    box-shadow: 0 12px 40px rgba(0,0,0,0.15);
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const BackBtn = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.textSecondary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  cursor: pointer;
  padding: 0;
`;

const ProviderChip = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 999px;
  background: #EFF4FF;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  margin-bottom: ${({ theme }) => theme.spacing.md};
`;

const Title = styled.h1`
  font-size: 22px;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.sm};
`;

const SubTitle = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: 0 0 ${({ theme }) => theme.spacing.xl};
`;

const Section = styled.section`
  padding: ${({ theme }) => theme.spacing.lg};
  background: #FAFBFF;
  border: 1px solid ${({ theme }) => theme.colors.borderLight};
  border-radius: ${({ theme }) => theme.radius.md};
  margin-bottom: ${({ theme }) => theme.spacing.base};
`;

const SectionTitle = styled.h3`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: ${({ theme }) => theme.fontSize.lg};
  font-weight: ${({ theme }) => theme.fontWeight.bold};
  color: ${({ theme }) => theme.colors.text};
  margin: 0 0 ${({ theme }) => theme.spacing.md};
`;

const Num = styled.span`
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  font-size: 12px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: ${({ theme }) => theme.fontWeight.bold};
`;

const SectionSub = styled.p`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin: -4px 0 ${({ theme }) => theme.spacing.md};
`;

const FieldGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.md};

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const Field = styled.div`
  ${({ $full }) => $full && 'grid-column: 1 / -1;'}
`;

const Label = styled.label`
  display: block;
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
`;

const Req = styled.span`
  color: ${({ theme }) => theme.colors.danger};
`;

const Hint = styled.span`
  color: ${({ theme }) => theme.colors.textLight};
  font-weight: ${({ theme }) => theme.fontWeight.regular};
  margin-left: 4px;
`;

const Input = styled.input`
  width: 100%;
  padding: 12px 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.base};
  background: ${({ $locked }) => ($locked ? '#F5F7FA' : '#fff')};
  color: ${({ $locked, theme }) => ($locked ? theme.colors.textSecondary : theme.colors.text)};
  outline: none;

  &:focus { border-color: ${({ $locked, theme }) => ($locked ? theme.colors.border : theme.colors.primary)}; }
`;

const VerifiedBadge = styled.span`
  display: inline-flex;
  align-items: center;
  margin-left: 8px;
  padding: 2px 8px;
  border-radius: 999px;
  background: #DCFCE7;
  color: #15803D;
  font-size: 11px;
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
`;

const ServiceGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.sm};

  @media (min-width: 768px) {
    grid-template-columns: 1fr 1fr 1fr;
  }
`;

const ServiceItem = styled.button`
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 12px 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.medium};
  cursor: pointer;
  text-align: left;
  ${({ $active, theme }) =>
    $active
      ? `background: ${theme.colors.primary}14; border: 1.5px solid ${theme.colors.primary}; color: ${theme.colors.primary};`
      : `background: #fff; border: 1px solid ${theme.colors.border}; color: ${theme.colors.text};`}
`;

const TypeGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const TypeBtn = styled.button`
  padding: 14px;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.base};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  ${({ $active, theme }) =>
    $active
      ? `background: ${theme.colors.primary}14; border: 1.5px solid ${theme.colors.primary}; color: ${theme.colors.primary};`
      : `background: #fff; border: 1px solid ${theme.colors.border}; color: ${theme.colors.text};`}
`;

const FileRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const FilePreview = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  flex: 1;
  min-width: 0;
  padding: 12px 14px;
  border: 1px solid ${({ theme }) => theme.colors.border};
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ $selected, theme }) => ($selected ? theme.colors.text : theme.colors.textLight)};
  background: #fff;

  span {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const FileClearBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  border: none;
  border-radius: 50%;
  background: ${({ theme }) => theme.colors.borderLight};
  color: ${({ theme }) => theme.colors.textSecondary};
  cursor: pointer;
  flex-shrink: 0;
`;

const FilePickBtn = styled.button`
  padding: 0 16px;
  border: 1px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.radius.md};
  background: #fff;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  white-space: nowrap;
  flex-shrink: 0;
`;

const TermsAll = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
  padding-bottom: ${({ theme }) => theme.spacing.sm};
  border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
`;

const Check = styled.div`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  border: 1.5px solid ${({ $checked, theme }) => ($checked ? theme.colors.primary : theme.colors.border)};
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ $checked }) => ($checked ? '#EFF4FF' : '#fff')};
  flex-shrink: 0;
  cursor: pointer;
`;

const TermsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding-top: ${({ theme }) => theme.spacing.sm};
`;

const TermsRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.sm};
`;

const TermsLabel = styled.span`
  flex: 1;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.text};
  cursor: pointer;
`;

const TermsLink = styled(Link)`
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: ${({ theme }) => theme.fontWeight.medium};
`;

const SubmitBtn = styled.button`
  width: 100%;
  padding: 16px;
  background: ${({ theme }) => theme.colors.primary};
  color: #fff;
  border: none;
  border-radius: ${({ theme }) => theme.radius.md};
  font-size: ${({ theme }) => theme.fontSize.md};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  margin-top: ${({ theme }) => theme.spacing.base};

  &:disabled {
    background: ${({ theme }) => theme.colors.borderLight};
    color: ${({ theme }) => theme.colors.textLight};
    cursor: not-allowed;
  }
`;

const FooterHint = styled.div`
  text-align: center;
  font-size: ${({ theme }) => theme.fontSize.sm};
  color: ${({ theme }) => theme.colors.textSecondary};
  margin-top: ${({ theme }) => theme.spacing.base};
`;

const FooterLink = styled.button`
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.primary};
  font-size: ${({ theme }) => theme.fontSize.sm};
  font-weight: ${({ theme }) => theme.fontWeight.semibold};
  cursor: pointer;
  padding: 0;
`;
